import qs from 'querystringify';
import { simpleAjaxDispatch } from './http';

// Fetch all packages (without groups) for table display
export function fetchScreeningPackagesBulk(orgId) {
  const qsData = { org_id: orgId };
  const qsFinal = qs.stringify(qsData, true);
  return simpleAjaxDispatch(
    `/screening_package/bulk${qsFinal}`,
    'fetchScreeningPackagesBulk',
    { meta: { orgId } }
  );
}

// Screening Packages
export function fetchScreeningPackages(orgId) {
  const qsData = { org_id: orgId };
  const qsFinal = qs.stringify(qsData, true);
  return simpleAjaxDispatch(
    `/screening_package${qsFinal}`,
    'fetchScreeningPackages',
    { meta: { orgId } }
  );
}

export function createScreeningPackage(json) {
  return simpleAjaxDispatch(
    '/screening_package',
    'createScreeningPackage',
    { method: 'POST', json, jsonSnake: false }
  );
}

export function updateScreeningPackage(id, json) {
  return simpleAjaxDispatch(
    '/screening_package',
    'updateScreeningPackage',
    { method: 'PUT', json: { ...json, id }, meta: { id }, jsonSnake: false }
  );
}

export function deleteScreeningPackage(id) {
  const qsData = { id };
  const qsFinal = qs.stringify(qsData, true);
  return simpleAjaxDispatch(
    `/screening_package${qsFinal}`,
    'deleteScreeningPackage',
    { method: 'DELETE', meta: { id } }
  );
}

// Dynamic Screening Groups
export function fetchDynamicScreeningGroups(orgId) {
  const qsData = { org_id: orgId, dynamic_only: true };
  const qsFinal = qs.stringify(qsData, true);
  return simpleAjaxDispatch(
    `/screening_group${qsFinal}`,
    'fetchDynamicScreeningGroups',
    { meta: { orgId } }
  );
}

// Screening Groups
export function fetchScreeningGroups(orgId) {
  const qsData = { org_id: orgId };
  const qsFinal = qs.stringify(qsData, true);
  return simpleAjaxDispatch(
    `/screening_group${qsFinal}`,
    'fetchScreeningGroups',
    { meta: { orgId } }
  );
}

export function createScreeningGroup(json) {
  return simpleAjaxDispatch(
    '/screening_group',
    'createScreeningGroup',
    { method: 'POST', json }
  );
}

export function updateScreeningGroup(id, json) {
  return simpleAjaxDispatch(
    '/screening_group',
    'updateScreeningGroup',
    { method: 'PUT', json: { ...json, id }, meta: { id } }
  );
}

export function deleteScreeningGroup(id) {
  const qsData = { id };
  const qsFinal = qs.stringify(qsData, true);
  return simpleAjaxDispatch(
    `/screening_group${qsFinal}`,
    'deleteScreeningGroup',
    { method: 'DELETE', meta: { id } }
  );
}

// Accreditation Definitions (from /scan endpoint)
export function fetchAccreditationDefinitions(orgId) {
  const qsData = { internal_only: true };
  const qsFinal = qs.stringify(qsData, true);
  return simpleAjaxDispatch(
    `/scan${qsFinal}`,
    'fetchAccreditationDefinitions',
    { meta: { orgId } }
  );
}

// Screening Provider Settings
export function fetchScreeningProviderSettings(orgId) {
  const qsData = { org_id: orgId };
  const qsFinal = qs.stringify(qsData, true);
  return simpleAjaxDispatch(
    `/screening_provider_settings${qsFinal}`,
    'fetchScreeningProviderSettings',
    { meta: { orgId } }
  );
}

// Update guiding text for a specific member in a group
export function updateMemberGuidingText(groupId, memberId, guidingText, allGroups) {
  return (dispatch) => {
    // Find the group
    const group = allGroups.find(g => g.id === groupId);
    if (!group) {
      return Promise.reject(new Error('Group not found'));
    }

    // Update the member's guiding text
    const updatedMembers = (group.screeningGroupMembers || []).map(member => {
      if (member.id === memberId) {
        return {
          type: member.type,
          guiding_text: guidingText || null,
        };
      }
      return {
        type: member.type,
        guiding_text: member.guidingText || null,
      };
    });

    // Update the entire group with the modified members
    const groupUpdateData = {
      name: group.name,
      code: group.code,
      group_members: updatedMembers,
    };

    return dispatch(updateScreeningGroup(groupId, groupUpdateData));
  };
}

// Update group name
export function updateGroupName(groupId, newName, allGroups) {
  return (dispatch) => {
    // Find the group
    const group = allGroups.find(g => g.id === groupId);
    if (!group) {
      return Promise.reject(new Error('Group not found'));
    }

    // Preserve existing members with their guiding text
    const existingMembers = (group.screeningGroupMembers || []).map(member => ({
      type: member.type,
      guiding_text: member.guidingText || null,
    }));

    // Update the group name
    const groupUpdateData = {
      name: newName,
      code: group.code,
      group_members: existingMembers,
    };

    return dispatch(updateScreeningGroup(groupId, groupUpdateData));
  };
}

// Update group attachment requirements
export function updateGroupAttachments(groupId, minCount, maxCount, allGroups) {
  return (dispatch) => {
    // Find the group
    const group = allGroups.find(g => g.id === groupId);
    if (!group) {
      return Promise.reject(new Error('Group not found'));
    }

    // Preserve existing members with their guiding text
    const existingMembers = (group.screeningGroupMembers || []).map(member => ({
      type: member.type,
      guiding_text: member.guidingText || null,
    }));

    // Update attachment requirements
    const groupUpdateData = {
      name: group.name,
      code: group.code,
      min_attachment_count: minCount,
      max_attachment_count: maxCount,
      group_members: existingMembers,
    };

    return dispatch(updateScreeningGroup(groupId, groupUpdateData));
  };
}

// Export screening configuration
export function exportScreeningConfiguration(orgId, orgName) {
  const qsData = { org_id: orgId };
  const qsFinal = qs.stringify(qsData, true);

  return (dispatch) => {
    return dispatch(simpleAjaxDispatch(
      `/screening_package/export${qsFinal}`,
      'exportScreeningConfiguration',
      { method: 'POST', meta: { orgId } }
    ))
      .then((response) => {
        if (response && response.data) {
          // Trigger file download
          const blob = new Blob([JSON.stringify(response.data, null, 2)], {
            type: 'application/json',
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const date = new Date().toISOString().split('T')[0];
          const sanitizedOrgName = (orgName || 'org').replace(/[^a-z0-9]/gi, '_').toLowerCase();
          link.download = `screening_config_${sanitizedOrgName}_${date}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          return response;
        }
        throw new Error('Export failed - no data received');
      })
      .catch((error) => {
        throw error;
      });
  };
}

// Helper to convert camelCase to snake_case for backend
function toSnakeCase(obj) {
  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      acc[snakeKey] = toSnakeCase(obj[key]);
      return acc;
    }, {});
  }
  return obj;
}

// Clear all screening packages
export function clearAllScreeningPackages(orgId) {
  const qsData = { org_id: orgId };
  const qsFinal = qs.stringify(qsData, true);

  return simpleAjaxDispatch(
    `/screening_package/bulk${qsFinal}`,
    'clearAllScreeningPackages',
    { method: 'DELETE', meta: { orgId } }
  );
}

// Import screening configuration
export function importScreeningConfiguration(orgId, fileContent) {
  const qsData = { org_id: orgId };
  const qsFinal = qs.stringify(qsData, true);

  return (dispatch) => {
    let parsedContent;
    try {
      parsedContent = typeof fileContent === 'string' ? JSON.parse(fileContent) : fileContent;

      // Convert camelCase to snake_case for backend
      parsedContent = toSnakeCase(parsedContent);
    } catch (error) {
      return Promise.reject(new Error('Invalid JSON file'));
    }

    return dispatch(simpleAjaxDispatch(
      `/screening_package/import${qsFinal}`,
      'importScreeningConfiguration',
      { method: 'POST', json: parsedContent, meta: { orgId } }
    ))
      .then((response) => {
        // Refresh the screening packages and groups after import
        dispatch(fetchScreeningPackagesBulk(orgId));
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };
}
